// EmployerAddEdit.jsx (Refactored)

import { yupResolver } from "@hookform/resolvers/yup";
import ImageResize from 'quill-image-resize-module-react';
import { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "react-google-autocomplete";
import { useForm } from "react-hook-form";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from "yup";
import { useCreateEmployerMutation, useUpdateEmployerMutation } from "../store/apiSlice";
import { setEmployer } from "../store/postsSlice";
import SearchResults2 from "./SearchResults2";

// Mock Data (kept from original)
const users = [
  { id: 76, firstName: "Mary Rose Joana" },
  { id: 77, firstName: "Joan" },
  { id: 182, firstName: "Clarissa" },
  { id: 79, firstName: "Fausto" },
  { id: 81, firstName: "Jhon Michael" },
  { id: 82, firstName: "Bianca" },
  { id: 83, firstName: "Laira Andrea" },
  { id: 184, firstName: "Rencel Velasco" },
  { id: 86, firstName: "Don" },
  { id: 4, firstName: "Jarrod Kanizay" },
];
const salesAgents = [
  { name: 'Jarrod', id: 4 },
  { name: 'Joe', id: 16519 }
];

//================================================================//
// 1. STYLING & UI HELPER COMPONENTS
//================================================================//

// Tab Button (from previous refactor)
const TabButton = ({ label, isActive, onClick }) => {
  const baseClasses = "flex-grow py-2.5 px-4 text-sm font-semibold text-center transition-colors duration-200 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50";
  const activeClasses = "bg-red-500 text-white shadow-sm";
  const inactiveClasses = "text-gray-600 hover:bg-gray-200";
  return (
    <button className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} onClick={onClick}>
      {label}
    </button>
  );
};

// Form Section Wrapper
const FormSection = ({ title, children }) => (
  <div className="pt-6 mt-6 border-t border-gray-200">
    <h3 className="text-xl font-bold text-red-600 mb-6">{title}</h3>
    <div className="space-y-6">{children}</div>
  </div>
);

// Form Field Layout (Label on left, Input on right - responsive)
const FormField = ({ label, children, required = false, description = null }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 items-start">
    <label className="block text-sm font-semibold text-gray-700 md:text-right">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="md:col-span-2">
      {children}
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
    </div>
  </div>
);

// Standardized Input Component
const FormInput = ({ field, register, errors, ...props }) => (
  <>
    <input
      {...register(field)}
      {...props}
      className={`w-full px-3 py-2 border ${errors[field] ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500`}
    />
    {errors[field] && <p className="text-xs text-red-500 mt-1">{errors[field].message}</p>}
  </>
);

// Standardized Select Component
const FormSelect = ({ field, register, errors, children, ...props }) => (
  <select
    {...register(field)}
    {...props}
    className={`w-full px-3 py-2 border ${errors[field] ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white`}
  >
    {children}
  </select>
);

// Standardized Radio Group Component
const FormRadioGroup = ({ field, register, options, isHorizontal = false }) => (
  <div className={`flex gap-x-6 gap-y-2 ${isHorizontal ? 'flex-row flex-wrap' : 'flex-col'}`}>
    {options.map(option => (
      <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
        <input
          {...register(field)}
          type="radio"
          value={option.value}
          className="radio radio-sm checked:bg-red-500"
        />
        <span>{option.label}</span>
      </label>
    ))}
  </div>
);


//================================================================//
// 2. TAB CONTENT COMPONENTS
//================================================================//

const ProfileTab = ({ employer, formMethods }) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = formMethods;
  const [logo, setLogo] = useState(employer?.logo || "");
  const dispatch = useDispatch();

  // Quill setup
  Quill.register('modules/imageResize', ImageResize);
  const quillModules = {
    toolbar: [
      [{ 'header': '1' }, { 'header': '2' }], ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }], ['link', 'image', 'video'], ['clean']
    ],
    imageResize: { parchment: Quill.import('parchment'), modules: ['Resize', 'DisplaySize'] }
  };

  const onUploadAvatar = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    try {
      const response = await fetch('https://api.sciencejobs.com.au/api/generate-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, directory: 'university-logo' })
      });
      const { presignedUploadUrl } = await response.json();
      await fetch(presignedUploadUrl, { method: 'PUT', body: file });
      setLogo(file.name);
      setValue("logo", file.name, { shouldValidate: true });
    } catch (error) {
      console.error("Error uploading logo:", error);
      // Handle error state in UI if needed
    }
  };

  // Checkbox options with logic
  const employerTypeOptions = [
    { field: 'unlimitedYN', label: 'Unlimited', onChange: (e) => { setValue("ranking", e.target.checked ? "5" : "3"); } },
    { field: 'catholicDiocesYN', label: 'Catholic Dioces', onChange: (e) => { setValue("ranking", e.target.checked ? "5" : "3"); } },
    { field: 'salesLeadClientYN', label: 'Sales Lead Client' }
  ];

  return (
    <form onSubmit={handleSubmit(formMethods.onSubmit)} noValidate>
      {/* Container for the floating button */}
      <div className="fixed top-4 right-4 z-50">
        <button
          type="submit"
          className="btn bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
        >
          {employer ? 'Save Changes' : 'Create Employer'}
        </button>
      </div>

      <div className="p-4 sm:p-6 bg-white rounded-lg shadow-md">
        <FormSection title="Basic Information">
          <FormField label="Institution Name" required>
            <FormInput field="company_name" register={register} errors={errors} placeholder="e.g., University of Melbourne" />
          </FormField>

          <FormField label="Institution Location">
            <Autocomplete
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              defaultValue={employer?.location || ""}
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} // Use environment variables for API keys
              onPlaceSelected={(place) => {
                const components = place.address_components || [];
                const get = (type) => components.find(c => c.types.includes(type))?.long_name || "";
                setValue("location", place.formatted_address);
                setValue("suburb", get("locality"));
                setValue("state", get("administrative_area_level_1"));
                setValue("country", get("country"));
                setValue("postalCode", get("postal_code"));
                dispatch(setEmployer({ company_name: get("organization") || watch("company_name"), country: get("country") }));
              }}
              options={{ types: ["geocode", "establishment"] }}
            />
          </FormField>

          <FormField label="Logo">
            <div className="flex items-center gap-4">
              {logo && (
                <img
                  src={`https://academicjobs.s3.amazonaws.com/img/university-logo/${logo}`}
                  alt="logo"
                  className="w-20 h-20 object-contain rounded-md bg-gray-100 p-1 border"
                />
              )}
              <div className="flex-grow">
                {employer?.company_name && <a className="text-gray-600 underline hover:text-blue-600" target="_blank" href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(employer?.company_name)} ${encodeURIComponent(employer?.location)}Logo`}>Google image link: {employer.company_name}</a>}
                <FormInput field="logo" register={register} errors={errors} placeholder="logo-file-name.png" />
                <input type="file" onChange={onUploadAvatar} className="text-sm mt-2 file-input file-input-bordered file-input-xs w-full max-w-xs" />
              </div>
            </div>
          </FormField>

          <FormField label="Ranking">
            <FormRadioGroup field="ranking" register={register} isHorizontal options={[
              { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' },
              { value: '4', label: '4' }, { value: '5', label: '5' }
            ]} />
          </FormField>
          <FormField label="Government level">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" {...register("govLevel")} value="Federal" className="radio radio-sm" /><span className="label-text font-bold text-sm">Federal</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" {...register("govLevel")} value="State" className="radio radio-sm" /><span className="label-text font-bold text-sm">State</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" {...register("govLevel")} value="Local" className="radio radio-sm" /><span className="label-text font-bold text-sm">Local</span></label>
            </div>
          </FormField>

        </FormSection>
{/* 
        <FormSection title="Account Management">
          <FormField label="AJ Admin Agent">
            <FormSelect field="AJAdminAgent" register={register} errors={errors}>
              <option value="">Select an Agent</option>
              {users.map(user => <option key={user.id} value={user.id}>{user.firstName}</option>)}
            </FormSelect>
          </FormField>
          <FormField label="AJ Sales Agent">
            <FormSelect field="AJSalesAgent" register={register} errors={errors}>
              <option value="">Select an Agent</option>
              {salesAgents.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
            </FormSelect>
          </FormField>
        </FormSection> */}

        <FormSection title="Location Details (Auto-filled)">
          <FormField label="Full Address"><FormInput field="location" register={register} errors={errors} readOnly /></FormField>
          <FormField label="Country"><FormInput field="country" register={register} errors={errors} readOnly /></FormField>
          <FormField label="State/Province"><FormInput field="state" register={register} errors={errors} readOnly /></FormField>
          <FormField label="City"><FormInput field="city" register={register} errors={errors} readOnly /></FormField>
          <FormField label="Suburb"><FormInput field="suburb" register={register} errors={errors} readOnly /></FormField>
          <FormField label="Postal Code"><FormInput field="postalCode" register={register} errors={errors} readOnly /></FormField>
        </FormSection>

        <FormSection title="Employer Website & Socials">
          <FormField label="Website"><FormInput type="url" field="website" register={register} errors={errors} placeholder="https://..." /></FormField>
          <FormField label="Employer Job Page URL"><FormInput type="url" field="employerPageURL" register={register} errors={errors} placeholder="https://..." /></FormField>
          <FormField label="LinkedIn"><FormInput type="url" field="LinkedIn" register={register} errors={errors} placeholder="https://linkedin.com/company/..." /></FormField>
        </FormSection>

        <FormSection title="Company Description">
          <div className="bg-white">
            <ReactQuill
              theme="snow"
              modules={quillModules}
              value={watch("company_description") || ""}
              onChange={(value) => setValue("company_description", value)}
            />
          </div>
        </FormSection>

        {/* The original button position has been removed from here */}
      </div>
    </form>
  );
};


const JobsTab = ({ employerId }) => (
  <div className="p-4 sm:p-6 bg-white rounded-lg shadow-md">
    <SearchResults2 q={{ employer_id: employerId, expired: "0" }} />
  </div>
);


//================================================================//
// 3. MAIN EXPORTED COMPONENT
//================================================================//

const EmployerAddEdit = ({ employer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "profile");
  const [activeJobId, setActiveJobId] = useState(location.state?.activeJobId || null);

  // --- NEW: State for managing active tab and user data for invoice ---
  const [userToInvoice, setUserToInvoice] = useState(null);

  // NEW: Ref to store the timestamp when the component mounts.
  const mountTimeRef = useRef(null);

  // NEW: useEffect to set the mount time once the component is rendered.
  // This serves as the start time for the creation/update duration.
  useEffect(() => {
    mountTimeRef.current = Date.now();
  }, []); // Empty dependency array ensures this runs only once on mount.

  // --- NEW: Function to handle switching to the invoice tab ---
  const handleNavigateToInvoice = (user) => {
    console.log("Navigating to invoice for user:", user);
    setUserToInvoice(user); // Set the user data
    setActiveTab('invoice'); // Switch to the invoice tab
  };

  const isAddMode = !employer;
  const userInfo = useSelector((state) => state.auth.userInfo);

  const [updateEmployer] = useUpdateEmployerMutation();
  const [createEmployer] = useCreateEmployerMutation();

  const validationSchema = yup.object({
    company_name: yup.string().required('Institution name is required.'),
    // Add other validations as needed
  }).required();

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: useMemo(() => employer || {
      ranking: "3",
      clientType: 'HeadlineOnly',
      postedBy: userInfo?.id
    }, [employer, userInfo]),
  });

  // Reset form when employer data changes
  useEffect(() => {
    if (employer) {
      formMethods.reset(employer);
    }
  }, [employer, formMethods.reset]);

  // Submission handler
  const onSubmit = async (data) => {
    try {

      if (isAddMode) {
        const payload = {
          ...data,
        };
        await createEmployer(payload).unwrap();
        navigate("/employers-center/");
      } else {
        const payload = {
          id: employer.id,
          ...data,
        };
        await updateEmployer(payload).unwrap();
        navigate(`/employers-center/${employer.id}/`);
      }
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Failed to save employer:", err);
      alert("Failed to save employer. Please try again.");
    }
  };

  // Add onSubmit to formMethods so we can pass it down
  formMethods.onSubmit = onSubmit;

  const TABS = [
    { id: 'profile', label: 'Profile' },
    { id: 'jobs', label: 'Jobs', requiresEmployer: true },
  ];

  const availableTabs = TABS.filter(tab => !tab.requiresEmployer || !isAddMode);

  return (
    <div className="w-full mx-auto py-8">
      {/* Tab Navigation */}
      <div className="px-2 mb-6">
        <div className="bg-gray-100 p-1.5 rounded-xl shadow-inner">
          <div className="flex space-x-1">
            {availableTabs.map((tab) => (
              <TabButton
                key={tab.id}
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "profile" && <ProfileTab employer={employer} formMethods={formMethods} />}
        {!isAddMode && activeTab === "jobs" && <JobsTab employerId={employer.id} />}
      </div>
    </div>
  );
};

export default EmployerAddEdit;